import { BooleanGambit, KeyValueGambit, GambitType } from 'flarum/common/query/IGambit';
import { canHandleSupportTickets } from '../utils/permissions';
export const SUPPORT_TICKETS_RESOURCE = 'linkrobins-support-tickets';

/**
 * Filter support tickets by status: is:open, is:in-progress, is:awaiting-response, is:resolved, is:closed.
 * Accepts both kebab-case and snake_case aliases. Supports negation (e.g. -is:closed).
 */
export class StatusGambit {
  type = (() => GambitType.Grouped)();
  predicates = false;
  key() {
    return ['open', 'in-progress', 'awaiting-response', 'resolved', 'closed'];
  }
  filterKey() {
    return 'status';
  }
  booleanKey() {
    return 'is';
  }
  groupKey() {
    return app.translator && app.translator.trans('core.lib.gambits.group_keys.' + this.booleanKey(), {}, true) || 'is';
  }
  pattern() {
    const is = this.groupKey();
    return `${is}:(open|in[-_]progress|awaiting[-_](?:response|user)|resolved|closed)`;
  }
  toFilter(matches, negate) {
    const key = (negate ? '-' : '') + this.filterKey();
    let val = matches[1].toLowerCase().replace('-', '_');
    if (val === 'awaiting_response') {
      val = 'awaiting_user';
    }
    return {
      [key]: val
    };
  }
  fromFilter(value, negate) {
    const is = this.groupKey();
    const normalize = v => {
      if (v === 'awaiting_user' || v === 'awaiting_response') return 'awaiting-response';
      if (v === 'in_progress') return 'in-progress';
      return v;
    };
    if (Array.isArray(value)) {
      return value.map(v => `${negate ? '-' : ''}${is}:${normalize(v)}`).join(' ');
    }
    return `${negate ? '-' : ''}${is}:${normalize(value)}`;
  }
  suggestion() {
    return {
      group: this.groupKey(),
      key: this.key()
    };
  }
  enabled() {
    return true;
  }
}
function safeTrans(key, fallback) {
  const fullKey = 'linkrobins-support.lib.gambits.tickets.' + key;
  return app.translator?.translations?.[fullKey] && app.translator.trans(fullKey, {}, true) || fallback;
}

/**
 * Key-value assignee filter: assigned:me, assigned:none, assigned:unassigned (staff only).
 */
export class AssignedGambit extends KeyValueGambit {
  key() {
    return safeTrans('assigned.key', 'assigned');
  }
  hint() {
    return safeTrans('assigned.hint', 'me or none');
  }
  filterKey() {
    return 'assigned';
  }
  pattern() {
    return `${this.key()}:(me|none|unassigned)`;
  }
  toFilter(matches, negate) {
    const key = (negate ? '-' : '') + this.filterKey();
    const val = matches[1] === 'unassigned' ? 'none' : matches[1];
    return {
      [key]: val
    };
  }
  fromFilter(value, negate) {
    if (value === 'me') {
      return `${negate ? '-' : ''}${this.key()}:me`;
    }
    if (value === 'none' || value === 'unassigned') {
      return `${negate ? '-' : ''}${this.key()}:none`;
    }
    return '';
  }
  enabled() {
    return canHandleSupportTickets();
  }
}

/**
 * Staff-only filter for own filed tickets: is:mine.
 */
export class MineGambit extends BooleanGambit {
  key() {
    return safeTrans('mine.key', 'mine');
  }
  filterKey() {
    return 'mine';
  }
  toFilter(_matches, negate) {
    const key = (negate ? '-' : '') + this.filterKey();
    return {
      [key]: '1'
    };
  }
  fromFilter(_value, negate) {
    const is = this.groupKey();
    return `${negate ? '-' : ''}${is}:${this.key()}`;
  }
  enabled() {
    return canHandleSupportTickets();
  }
}

/**
 * Filter tickets by ticket type driver: type:standard, type:appeal, etc.
 */
export class TicketTypeGambit extends KeyValueGambit {
  key() {
    return safeTrans('type.key', 'type');
  }
  hint() {
    return safeTrans('type.hint', 'standard, appeal, or custom type');
  }
  filterKey() {
    return 'ticketType';
  }
}

/**
 * Staff-only filter for tickets opened by a specific username: author:<username>.
 */
export class AuthorGambit extends KeyValueGambit {
  key() {
    return safeTrans('author.key', 'author');
  }
  hint() {
    return safeTrans('author.hint', 'username');
  }
  filterKey() {
    return 'author';
  }
  enabled() {
    return canHandleSupportTickets();
  }
}

/**
 * Staff-only filter for tickets by priority: priority:urgent, priority:normal, priority:low.
 */
export class PriorityGambit extends KeyValueGambit {
  key() {
    return safeTrans('priority.key', 'priority');
  }
  hint() {
    return safeTrans('priority.hint', 'urgent, normal, or low');
  }
  filterKey() {
    return 'priority';
  }
  valuePattern() {
    return '(urgent|normal|low|Urgent|Normal|Low)';
  }
  gambitValueToFilterValue(value) {
    return value.toLowerCase();
  }
  fromFilter(value, negate) {
    if (typeof value === 'string') {
      return `${negate ? '-' : ''}${this.key()}:${value}`;
    }
    return '';
  }
  enabled() {
    return canHandleSupportTickets();
  }
}

/**
 * Filter tickets by category slug: category:<slug> or cat:<slug>.
 */
export class CategoryGambit extends KeyValueGambit {
  key() {
    return safeTrans('category.key', 'category');
  }
  hint() {
    return safeTrans('category.hint', 'slug');
  }
  filterKey() {
    return 'category';
  }
  pattern() {
    const key = this.key();
    return `(?:${key}|cat):([a-zA-Z0-9_-]+)`;
  }
  toFilter(matches, negate) {
    const key = (negate ? '-' : '') + this.filterKey();
    return {
      [key]: matches[1]
    };
  }
  fromFilter(value, negate) {
    if (typeof value === 'string') {
      return `${negate ? '-' : ''}${this.key()}:${value}`;
    }
    return '';
  }
}
flarum.reg.add('linkrobins-support', 'forum/query/ticketGambits', { SUPPORT_TICKETS_RESOURCE: SUPPORT_TICKETS_RESOURCE,StatusGambit: StatusGambit,AssignedGambit: AssignedGambit,MineGambit: MineGambit,TicketTypeGambit: TicketTypeGambit,AuthorGambit: AuthorGambit,PriorityGambit: PriorityGambit,CategoryGambit: CategoryGambit, });